/**
 * CMS copy helpers — Website General Data options with hard-coded fallbacks
 * when fields are empty (so the site still renders before editors fill CMS).
 */

export const DEFAULT_DEVELOPMENT_BANNER_TEXT =
  'Dashboard in Development – Updates Ongoing – Data and Features Under Refinement';

/** Shared browser brand labels (usually left as-is across instruction languages). */
const DEFAULT_BROWSER_LABELS = {
  chromeLabel: 'Chrome / Edge',
  safariLabel: 'Safari',
  firefoxLabel: 'Firefox',
};

/**
 * Defaults for Language menu instruction copy (Albanian / Serbian).
 * Desktop vs mobile steps differ by real device type (not window width).
 * Sources: Chrome Help, Apple Safari User Guide, Edge Translator behavior.
 */
export const DEFAULT_LANGUAGE_HINT_LOCALES = {
  sq: {
    optionLabel: 'Shqip',
    dismissLabel: 'E kuptova',
    lead: 'Kjo faqe është në anglisht. Përktheni me shfletuesin tuaj në shqip.',
    // Desktop: right-click is most reliable; address-bar Translate icon also works
    chromeDesktop:
      'Kliko me të djathtë në faqe → Translate to… → Shqip (ose ikona Translate në shiritin e adresës)',
    // Mobile: overflow menu → Translate; optional auto-offer bar
    chromeMobile: 'Troju (⋮) → Translate → Shqip (ose shiriti Translate nëse shfaqet)',
    // Desktop Safari: Translate in Smart Search field; View → Translation as fallback
    safariDesktop:
      'Ikona Translate në shiritin e adresës → Shqip (ose View → Translation)',
    // iPhone/iPad: Page Menu (aA) → Translate to…
    safariMobile: 'Prekni aA në shiritin e adresës → Translate to… → Shqip',
    firefoxDesktop:
      'Firefox nuk përkthen faqe vetë — përdorni një shtesë, ose hapni në Chrome / Edge',
    firefoxMobile:
      'Firefox nuk përkthen faqe vetë — hapeni në Chrome ose Safari',
  },
  sr: {
    optionLabel: 'Srpski',
    dismissLabel: 'Razumem',
    lead: 'Ovaj sajt je na engleskom. Prevedite ga pregledačem na srpski.',
    chromeDesktop:
      'Desni klik na stranici → Translate to… → Srpski (ili ikona Translate u adresnoj traci)',
    chromeMobile: 'Meni (⋮) → Translate → Srpski (ili traka Translate ako se pojavi)',
    safariDesktop:
      'Ikona Translate u adresnoj traci → Srpski (ili View → Translation)',
    safariMobile: 'Dodirnite aA u adresnoj traci → Translate to… → Srpski',
    firefoxDesktop:
      'Firefox ne prevodi cele stranice sam — koristite dodatak, ili otvorite u Chrome / Edge',
    firefoxMobile:
      'Firefox ne prevodi cele stranice sam — otvorite u Chrome ili Safari',
  },
};

/** Old step strings that should be ignored if still stored in CMS. */
const LEGACY_LANGUAGE_HINT_STEPS = new Set([
  'Shiriti i adresës → Përkthe → Shqip',
  'Menuja (⋯) ose shiriti i përkthimit → Shqip',
  'View → Translation → zgjidhni Shqip',
  'Shiriti i adresës → aA → Përkthe → Shqip',
  'Përdorni një shtesë përkthimi, ose hapni në Chrome / Edge',
  'Përdorni një shtesë përkthimi, ose hapni në Chrome / Safari',
  'Adresna traka → Prevedi → Srpski',
  'Meni (⋯) ili traka za prevođenje → Srpski',
  'View → Translation → izaberite Srpski',
  'Adresna traka → aA → Prevedi → Srpski',
  'Koristite dodatak za prevod, ili otvorite u Chrome / Edge',
  'Koristite dodatak za prevod, ili otvorite u Chrome / Safari',
  'Address bar → Translate → Shqip or Srpski',
  'Menu (⋯) or translate bar → Shqip or Srpski',
  'View → Translation → choose language',
  'Address bar → aA → Translate → choose language',
  'Use a translate extension, or open in Chrome / Edge',
  'Use a translate extension, or open in Chrome / Safari',
]);

function localeField(g, code, baseName, fallback) {
  const raw = g[`language_hint_${baseName}_${code}`];
  if (raw === null || raw === undefined) return fallback;
  const s = String(raw).trim();
  if (s === '' || LEGACY_LANGUAGE_HINT_STEPS.has(s)) return fallback;
  return s;
}

export function buildLanguageHintCopy(generalData) {
  const g = generalData || {};
  const labels = DEFAULT_BROWSER_LABELS;
  const buildLocale = (code) => {
    const d = DEFAULT_LANGUAGE_HINT_LOCALES[code];
    return {
      optionLabel: localeField(g, code, 'option_label', d.optionLabel),
      dismissLabel: localeField(g, code, 'dismiss_label', d.dismissLabel),
      lead: localeField(g, code, 'lead', d.lead),
      chromeLabel: cmsText(g.language_hint_chrome_label, labels.chromeLabel),
      chromeDesktop: localeField(g, code, 'chrome_text', d.chromeDesktop),
      chromeMobile: localeField(g, code, 'chrome_text_mobile', d.chromeMobile),
      safariLabel: cmsText(g.language_hint_safari_label, labels.safariLabel),
      safariDesktop: localeField(g, code, 'safari_text', d.safariDesktop),
      safariMobile: localeField(g, code, 'safari_text_mobile', d.safariMobile),
      firefoxLabel: cmsText(g.language_hint_firefox_label, labels.firefoxLabel),
      firefoxDesktop: localeField(g, code, 'firefox_text', d.firefoxDesktop),
      firefoxMobile: localeField(g, code, 'firefox_text_mobile', d.firefoxMobile),
    };
  };

  return {
    menuLabel: cmsText(g.language_menu_label, 'Language'),
    chooseLabel: cmsText(
      g.language_hint_choose_label,
      'Zgjidhni gjuhën / Izaberite jezik'
    ),
    sq: buildLocale('sq'),
    sr: buildLocale('sr'),
  };
}

export function cmsText(value, fallback = '') {
  if (value === null || value === undefined) return fallback;
  const s = String(value).trim();
  return s !== '' ? s : fallback;
}

/** ACF true_false may be boolean, 1/0, or "1"/"0". */
export function cmsFlag(value, defaultValue = false) {
  if (value === null || value === undefined || value === '') return defaultValue;
  if (value === true || value === 1 || value === '1') return true;
  if (value === false || value === 0 || value === '0') return false;
  return Boolean(value);
}

/** Resolve ACF image / url field to a usable src string. */
export function cmsImageUrl(value, fallback = '') {
  if (value === null || value === undefined || value === '' || value === false) {
    return fallback;
  }
  if (typeof value === 'string') {
    const s = value.trim();
    return s !== '' ? s : fallback;
  }
  if (typeof value === 'object') {
    const url = value.url || value.sizes?.medium || value.sizes?.thumbnail;
    if (typeof url === 'string' && url.trim() !== '') return url.trim();
  }
  return fallback;
}

export function buildHomeCopy(generalData, goalType) {
  const isEU = goalType === 'eu';
  const g = generalData || {};
  const legendBase = cmsText(g.home_legend_heading, 'SDG Progress');
  const euSuffix = cmsText(g.home_legend_heading_eu_suffix, ' & Status Assessment');
  return {
    dashboardsTitle: cmsText(g.home_dashboards_section_title, 'SDG Dashboards and Trends'),
    dashboardsSubtitle: cmsText(
      g.home_dashboards_section_subtitle,
      'Click on a goal to view more information.'
    ),
    legendHeading: isEU ? `${legendBase}${euSuffix}` : legendBase,
    statusAssessmentBlurb: cmsText(
      g.home_status_assessment_blurb,
      'STATUS: Where Kosovo sits among EU-27 comparison countries (benchmark quintiles)'
    ),
    progressBlurb: cmsText(
      g.home_progress_blurb,
      'PROGRESS: Direction and pace of progress toward each SDG target'
    ),
    frameworkHeading: cmsText(g.framework_switch_heading, 'Choose a framework to explore'),
    reportDownloadLabel: isEU
      ? cmsText(g.report_download_title_eu_sdg, 'Download Kosovo EU SDG Profile (PDF)')
      : cmsText(g.report_download_title_global_sdg, 'Download Kosovo SDG Profile (PDF)'),
    goalCardIndicatorsTitle: cmsText(g.home_goal_card_indicators_title, 'View All Indicators'),
    goalCardIndicatorsSubtitle: cmsText(
      g.home_goal_card_indicators_subtitle,
      'View all indicators for this goal'
    ),
    goalCardTargetsTitle: cmsText(
      g.home_goal_card_targets_title,
      'Indicators grouped by Targets'
    ),
    indicatorsSectionTitle: isEU
      ? cmsText(g.home_indicators_section_title_eu, 'EU SDG Indicators')
      : cmsText(g.home_indicators_section_title_un, 'UN SDG Indicators'),
    indicatorsSectionSubtitle: cmsText(
      g.home_indicators_section_subtitle,
      'Click the indicator to view the latest data'
    ),
    viewAllIndicatorsLink: cmsText(
      g.home_view_all_indicators_link,
      'View all indicators (including those without data yet)'
    ),
    noIndicatorsMessage: cmsText(
      g.home_no_indicators_message,
      'No indicators available for {type} SDGs'
    ).replace('{type}', isEU ? 'EU' : 'UN'),
    frameworkSelectedLabel: cmsText(g.framework_selected_label, 'Selected'),
    frameworkUnTitle: cmsText(g.framework_un_title, 'Sustainable Development Goals'),
    frameworkUnDescription: cmsText(
      g.framework_un_description,
      'Explore the 17 Sustainable Development Goals.'
    ),
    frameworkEuTitle: cmsText(g.framework_eu_title, 'European Union SDGs'),
    frameworkEuDescription: cmsText(
      g.framework_eu_description,
      'Explore the EU SDG indicators and monitoring framework.'
    ),
    frameworkUnLogo: cmsImageUrl(g.framework_un_logo, '/framework-logos/un-emblem.svg'),
    frameworkEuLogo: cmsImageUrl(g.framework_eu_logo, '/framework-logos/eu-flag.svg'),
  };
}
